import SettingsActionCreators from "@/Actions/Settings";
import UsersActionCreators from "@/Actions/User";
import RoleBasedDisplayWrapper from "@/Components/UI/RoleBasedDisplayWrapper";
import { checkCanMultiplePricingPlansFor } from "@/Components/Utils";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { handleMarkupMargin } from "@/Components/Utils/PricingCalculator";
import useDispatch from "@/Components/Utils/useDispatch";
import { GlobalState } from "@/Reducers/interface";
import ZDistributorProductPricing from "@/V2Components/templates/ZDrawers/ZProducts/molecules/ZDistributorProductPricing";
import { isProductFromEtilize } from "@/V2Utils/Products";
import TaxSelect from "@molecules/TaxSelect";
import ZRenderCustomFieldFormItem from "@organisms/ZRenderCustomFieldFormItem";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";
import {
	getProductFormItem,
	ItemImages,
	renderCostPriceInputGroup,
	renderSellPriceInputGroup
} from "@templates/ZDrawers/ZProducts";
import { UploadResult } from "@uppy/core";
import ZImage from "@atoms/ZImage";
import ZModal from "@atoms/ZModal";
import { DefaultErrorMessage, UppyFileMetaData } from "@/Contracts";
import {
	BundleProduct,
	commonProductFormKeys,
	Distributor,
	EditOrCloneProductDrawerMode,
	noOfDistributorsIntegrated,
	PricingOptions,
	PricingRadioOptions,
	Product,
	ProductBillingType,
	ProductPricingPlan,
	ProductSource,
	ProductType,
	ratailPriceLabel,
	RetailPriceInfo
} from "@/Contracts/Products";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import { useComponentDidUpdate, useTestEnvironment } from "@/Hooks";
import ZFileUpload from "@molecules/ZFileUpload";
import { UppyAllowedFileTypes as AllowedFileTypes } from "@molecules/ZFileUpload/interface";
import ZMessage from "@molecules/ZMessage";
import {
	EWidgets,
	ICustomFormItemProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import Col from "antd/lib/col";
import { FormInstance } from "antd/lib/form";
import Row from "antd/lib/row";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFeatureAccess } from "../useFeatureAccess";
import DynamicPricingOptions from "@/Components/DocumentsPage/Drawers/DocumentProductDrawers/DocumentProductDrawersComponents/DynamicPricingOptions/DynamicPricingOptions";

const useCommonFunctions = (
	form: FormInstance<any>,
	drawerState: EProductDrawerState,
	ProductPricing: React.ReactElement<
		any,
		string | React.JSXElementConstructor<any>
	> | null,
	bundledProductList: BundleProduct[],
	ProductBundle: (() => JSX.Element) | null,
	productPricingPlanList: ProductPricingPlan[] | null,
	mode?: EditOrCloneProductDrawerMode | null,
	isReadonly?: boolean,
	pricing?: ProductPricingPlan | null,
	drawerOpen?: boolean,
	pricingDisabled?: boolean
): Record<string, any> => {
	const dispatch = useDispatch();
	const isTesting = useTestEnvironment();

	const {
		isLoading,
		taxCategoriesList,
		defaultPreferences: oldPreferences,
		unitOfMeasureMapping,
		selectedEtilizeProduct,
		integrations,
		suppliers,
		companyPreferences,
		customFieldList
	} = useSelector((state: GlobalState) => ({
		isLoading: state.products.isLoading,
		taxCategoriesList: state.user.taxCategoriesList,
		companyPreferences:
			state.user.userCompany.settings.preference_settings
				?.product_preference_settings,
		defaultPreferences:
			state.user.userData?.user_settings?.preference_settings
				?.product_preference_settings,
		unitOfMeasureMapping:
			state.user.userCompany.settings.default_unit_of_measure_mapping,
		selectedEtilizeProduct: state.products.selectedEtilizeProduct,
		integrations: state.user.userCompany.integrations,
		suppliers: state.settings.suppliers,
		customFieldList:
			state.customFields.customFieldListWithoutFormulaFields.item
	}));

	const movePreferencesToGlobal = useFeatureFlag("move_preferences_global");
	const enable_bundles_enchancements = useFeatureFlag(
		"enable_bundles_enchancements"
	);

	const [currentActiveProductImage, setCurrentActiveProductImage] = useState<
		string | null
	>(null);
	const [productImagePreviewModalOpen, setProductImagePreviewModalOpen] =
		useState<boolean>(false);
	const [productImageUploadModalOpen, setProductImageUploadModalOpen] =
		useState<boolean>(false);
	const [productImageList, setProductImageList] = useState<string[]>([]);
	const [currentProduct, setCurrentProduct] = useState<
		Product | BundleProduct | null
	>(null);
	const [currentProductType, setCurrentProductType] = useState<ProductType>(
		ProductType.Software
	);
	const [showIndividualProductPricing, setShowIndividualProductPricing] =
		useState<boolean>(false);

	const [selectedDistributor, setSelectedDistributor] =
		useState<PricingRadioOptions | null>(null);
	const [selectedPricing, setSelectedPricing] = useState<
		| ProductPricingPlan
		| PricingOptions.Custom
		| PricingOptions.Catalog
		| null
	>(null);

	const [retailInfo, setRetailInfo] = useState<RetailPriceInfo | null>(null);

	const defaultPreferences = movePreferencesToGlobal
		? companyPreferences
		: oldPreferences;

	const isFromEtilize =
		currentProduct?.type !== ProductType.Bundle
			? (currentProduct as Product)?.pricing.some(
					pricing => !!pricing.distributor_pricing_details
			  )
			: false;

	const isAmazonProduct =
		currentProduct?.product_source === ProductSource.Amazon ||
		(currentProduct as Product)?.id?.includes(ProductSource.Amazon) ||
		(!!(currentProduct as Product)?.asin &&
			(currentProduct as Product)?.asin !== "NA");

	const hasTaxEditPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[AllRolePermissions.TAX_SETUP_UPDATE]
	);

	const showAddTaxCategoryDrawer = () => {
		dispatch(UsersActionCreators.addTaxCategoryDrawerOpened());
	};

	const closeAddTaxCategoryDrawer = () => {
		dispatch(UsersActionCreators.addTaxCategoryDrawerClosed());
	};

	const stopPropagation = (
		$event: React.MouseEvent<HTMLElement, MouseEvent> | Event
	) => {
		$event.stopPropagation();
	};

	const showProductImageUploadModal = () =>
		setProductImageUploadModalOpen(true);

	const closeProductImageUploadModal = () =>
		setProductImageUploadModalOpen(false);

	const showPreviewProductImageModal = (productImage: string) => {
		setProductImagePreviewModalOpen(true);
		setCurrentActiveProductImage(productImage);
	};

	const closePreviewProductImageModal = () => {
		setProductImagePreviewModalOpen(false);
		setCurrentActiveProductImage(null);
	};

	const deleteProductImage = (selectedProductImageDelete: string) => {
		ZModal.confirm({
			title: "Are you sure you want to delete this image?",
			onOk: () => {
				setProductImageList(prevState => [
					...prevState.filter(
						productImage =>
							productImage !== selectedProductImageDelete
					)
				]);
			},
			okType: "danger",
			okText: "Delete"
		});
	};

	const uploadProductImage = async (
		result: UploadResult<UppyFileMetaData>
	) => {
		try {
			if (!result?.successful?.[0]?.uploadURL?.length) {
				throw new Error(DefaultErrorMessage);
			}

			const files = result.successful.map(file => file.uploadURL);
			setProductImageList(prevState => [...prevState.concat(files)]);
			closeProductImageUploadModal();
		} catch (error) {
			console.error(error);
		}
	};

	const getFieldValues = (): Record<commonProductFormKeys, any> => {
		const keys: commonProductFormKeys[] = [
			"selected_cost_price",
			"sell_price",
			"quantity",
			"should_add_setup_price_to_margin",
			"selected_setup_price",
			"type",
			"margin",
			"markup",
			"show_individual_product_pricing",
			"supplier_id"
		];
		return form.getFieldsValue(keys) as Record<commonProductFormKeys, any>;
	};

	const getInitialTaxCategoryId = (): string | undefined => {
		if (
			!!currentProduct?.tax_category_id?.length &&
			!taxCategoriesList?.find(taxCategory => {
				return taxCategory.id === currentActiveProduct?.tax_category_id;
			})?.disabled
		) {
			return currentProduct?.tax_category_id;
		} else {
			return taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.findIndex(
						productType => productType === currentProductType
					) > -1
			)?.id;
		}
	};

	const getInitialUnitOfMeasure = (): string | undefined => {
		const curUOM =
			drawerState !== EProductDrawerState.BUNDLECHILDEDIT
				? (currentProduct as Product)?.unit_of_measure
				: (currentProduct as BundleProduct)?.item?.unit_of_measure;
		if (!!curUOM?.length) {
			return curUOM;
		} else {
			return unitOfMeasureMapping?.find(
				mapping => mapping.item_type === currentProductType
			)?.unit_of_measure;
		}
	};

	const handleTaxCategoryDrawerOnSubmit = (id: string) => {
		form.setFieldsValue({
			tax_category_id: id
		});
	};

	useEffect(() => {
		const preventFormSubmit = (e: any) => {
			if (e.key === "Enter") {
				e.preventDefault();
			}
		};
		if (productImageUploadModalOpen) {
			document.addEventListener("keypress", preventFormSubmit);
		}
		return () => {
			document.removeEventListener("keypress", preventFormSubmit);
		};
	}, [productImageUploadModalOpen]);

	const showAddSupplierDrawer = () =>
		dispatch(SettingsActionCreators.showAddOrEditSupplierDrawer());

	const ProductItemImages = () => (
		<Row className="mt-4 mb-6">
			<Col span={6}>
				<div className="mt-3">Product Images</div>
			</Col>
			<Col span={18}>
				<ItemImages
					productImageList={productImageList}
					showPreviewProductImageModal={showPreviewProductImageModal}
					deleteProductImage={deleteProductImage}
					showProductImageUploadModal={showProductImageUploadModal}
					drawerState={drawerState}
				/>
				{!isTesting &&
					drawerState !== EProductDrawerState.BUNDLECHILDEDIT && (
						<ZFileUpload
							maxNumberOfFiles={5}
							minNumberOfFiles={1}
							useModal={{
								visible: productImageUploadModalOpen,
								title: "Upload image"
							}}
							useURL
							allowedFileTypes={[AllowedFileTypes.IMAGE]}
							maxFileSize={26214400}
							allowMultipleUploads={true}
							onCancel={closeProductImageUploadModal}
							onUploadSuccess={uploadProductImage}
						/>
					)}
				{currentActiveProductImage &&
					currentActiveProductImage.length > 0 && (
						<ZModal
							visible={productImagePreviewModalOpen}
							footer={null}
							onCancel={closePreviewProductImageModal}
							size="xl"
							title="Image Preview"
						>
							<ZImage
								src={currentActiveProductImage}
								alt="Product"
								preview={false}
							/>
						</ZModal>
					)}
			</Col>
		</Row>
	);

	const setMarginAndAmount = () => {
		const values = getFieldValues();
		const { markup, margin } = handleMarkupMargin(
			values.selected_cost_price || 0,
			values.sell_price || 0,
			values.quantity || 1,
			!!values.should_add_setup_price_to_margin,
			values.selected_setup_price || 0
		);
		const marginAmount =
			((values.sell_price || 0) - (values.selected_cost_price || 0)) *
				(values.quantity || 1) +
			(!!values.should_add_setup_price_to_margin
				? values.selected_setup_price || 0
				: 0);
		form.setFieldsValue({
			markup,
			margin,
			margin_and_amount: {
				margin,
				marginAmount
			}
		});
	};

	const currentActiveProduct =
		currentProduct?.type !== ProductType.Bundle
			? (currentProduct as Product)
			: null;

	const isEtilizeHardwareProduct =
		isProductFromEtilize(currentActiveProduct) &&
		currentProductType === ProductType.Hardware;

	const hasDistributorPricing = currentActiveProduct
		? (currentActiveProduct as Product)?.pricing?.some(
				pricing => !!pricing.distributor_pricing_details
		  )
		: false;

	const showDynamicPricingSection = currentActiveProduct
		? isEtilizeHardwareProduct || hasDistributorPricing
		: !!selectedEtilizeProduct || !!isFromEtilize;

	const pricingPlanSuppliers = (currentProduct as Product)?.pricing
		?.map(p => p.supplier)
		.filter(Boolean);
	const suppliersWithCurrentProduct = pricingPlanSuppliers?.length
		? [
				...pricingPlanSuppliers,
				...(suppliers?.filter(
					s => !pricingPlanSuppliers.some(ps => ps.id === s.id)
				) ?? [])
		  ]
		: suppliers;

	const CostPriceInputGroup = renderCostPriceInputGroup({
		form,
		getFieldValues,
		bundledProductList,
		setMarginAndAmount,
		currentProductType,
		drawerState,
		resetSelectedDistributor: () =>
			selectedEtilizeProduct &&
			handleEtilizePricingChange(PricingOptions.Custom),
		showDynamicPricingSection,
		selectedDistributor,
		suppliers: suppliersWithCurrentProduct,
		showAddSupplierDrawer,
		pricingDisabled,
		isBundleEnhancementEnable: enable_bundles_enchancements,
		isAmazonProduct
	});

	const SellPriceInputGroup = renderSellPriceInputGroup({
		form,
		getFieldValues,
		bundledProductList,
		setMarginAndAmount,
		currentProductType,
		drawerState,
		retailInfo,
		pricingDisabled,
		isBundleEnhancementEnable: enable_bundles_enchancements
	});

	const resetDynamicForm = () => {
		if (drawerState !== EProductDrawerState.BUNDLECHILDEDIT) {
			const curProduct = currentProduct as Product;
			const defaultPricing =
				pricing ??
				curProduct?.pricing.find(pricing => {
					if (curProduct.type === ProductType.Bundle) return pricing;
					return selectedPricing === PricingOptions.Catalog
						? !pricing.distributor_pricing_details
						: selectedPricing === PricingOptions.Custom
						? pricing.vendor_name === selectedPricing
						: pricing.distributor_pricing_details &&
						  pricing.id === selectedPricing?.id;
				}) ??
				curProduct?.pricing[0] ??
				productPricingPlanList?.[0];
			const showIndividualProductPricing =
				curProduct?.show_individual_product_pricing ?? false;

			const shouldAddSetupPrice =
				curProduct?.should_add_setup_price_to_margin ??
				form.getFieldValue("should_add_setup_price_to_margin") ??
				false;

			const { markup, margin } = handleMarkupMargin(
				defaultPricing?.cost_price ?? 0,
				defaultPricing?.sell_price ?? 0,
				1,
				showIndividualProductPricing,
				defaultPricing?.setup_price ?? 0
			);
			const marginAmount =
				((defaultPricing?.sell_price ?? 0) -
					(defaultPricing?.cost_price ?? 0)) *
					1 +
				(!!shouldAddSetupPrice ? defaultPricing?.setup_price ?? 0 : 0);

			const isEditOrCloneOrEtilizeAddDrawer = [
				EProductDrawerState.EDITORCLONE,
				EProductDrawerState.ETILIZEADD
			].includes(drawerState);

			const calculatedSellPrice =
				drawerState === EProductDrawerState.ETILIZEADD ||
				defaultPricing?.cost_price === defaultPricing?.sell_price
					? (markup ?? 0) * 0.01 * (defaultPricing?.cost_price ?? 0) +
					  (defaultPricing?.cost_price ?? 0)
					: defaultPricing?.sell_price;
			form.setFieldsValue({
				selected_cost_price: defaultPricing?.cost_price ?? 0,
				sell_price: calculatedSellPrice,
				quantity: 1,
				should_add_setup_price_to_margin:
					currentProduct?.should_add_setup_price_to_margin ?? true,
				markup: isEditOrCloneOrEtilizeAddDrawer
					? markup
					: defaultPreferences?.markup ?? 0,
				margin: isEditOrCloneOrEtilizeAddDrawer
					? margin
					: defaultPreferences?.margin ?? 0,
				margin_and_amount: {
					margin,
					marginAmount
				},
				vendor_name: defaultPricing?.vendor_name ?? "",
				vendor_sku_no: defaultPricing?.vendor_sku_no ?? "",
				supplier_id: defaultPricing?.supplier_id ?? null,
				selected_billing_period:
					defaultPricing?.billing_period ??
					ProductBillingType.OneTime,
				selected_setup_price: defaultPricing?.setup_price ?? 0,
				show_individual_product_pricing: showIndividualProductPricing,
				asin: curProduct?.asin
			});
			const retailPrice = defaultPricing?.distributor_pricing_details
				?.retail_price
				? ({
						distributor:
							defaultPricing.distributor_pricing_details
								?.distributor,
						label: ratailPriceLabel[
							defaultPricing.distributor_pricing_details
								?.distributor
						],
						price: defaultPricing.distributor_pricing_details
							?.retail_price,
						vendor_name:
							defaultPricing.distributor_pricing_details
								.vendor_name
				  } as RetailPriceInfo)
				: null;
			setRetailInfo(retailPrice);
			if (drawerState === EProductDrawerState.ETILIZEADD) {
				const distributorValue =
					(defaultPricing?.distributor_pricing_details
						?.distributor as PricingRadioOptions) || null;

				setSelectedDistributor(distributorValue);
				setSelectedPricing(defaultPricing || null);
			}
		} else {
			const curProduct = currentProduct as BundleProduct;
			const isAmazonProduct =
				currentProduct?.product_source === ProductSource.Amazon;
			const pricing = curProduct?.item?.pricing.find(
				pricing => pricing.id === curProduct?.selected_pricing_id
			);

			const { markup, margin } = isAmazonProduct
				? {
						markup: defaultPreferences?.markup,
						margin: defaultPreferences?.margin
				  }
				: handleMarkupMargin(
						curProduct?.selected_cost_price ?? 0,
						curProduct?.sell_price ?? 0,
						curProduct?.quantity ?? 1,
						showIndividualProductPricing,
						curProduct?.selected_setup_price ?? 0
				  );
			const marginAmount =
				((curProduct?.sell_price ?? 0) -
					(curProduct?.selected_cost_price ?? 0)) *
					1 +
				(!!currentProduct?.should_add_setup_price_to_margin
					? curProduct?.selected_setup_price ?? 0
					: 0);

			const calculatedSellPrice =
				(defaultPreferences?.markup ?? 0) *
					0.01 *
					(curProduct?.selected_cost_price ?? 0) +
				(curProduct?.selected_cost_price ?? 0);

			const productSellPrice =
				(defaultPreferences?.markup ?? 0) > 0 &&
				(defaultPreferences?.margin ?? 0) > 0 &&
				isAmazonProduct
					? calculatedSellPrice
					: !isAmazonProduct
					? curProduct?.sell_price
					: curProduct?.selected_cost_price;

			form.setFieldsValue({
				selected_cost_price: curProduct?.selected_cost_price ?? 0,
				supplier_id:
					(curProduct?.supplier?.id ||
						curProduct?.supplier_id ||
						pricing?.supplier_id) ??
					null,
				vendor_sku_no:
					(curProduct?.vendor_sku_no || pricing?.vendor_sku_no) ?? "",
				sell_price: productSellPrice ?? 0,
				quantity: curProduct?.quantity ?? 1,
				should_add_setup_price_to_margin:
					curProduct?.should_add_setup_price_to_margin ?? true,
				markup: markup ?? defaultPreferences?.markup ?? 0,
				margin: margin ?? defaultPreferences?.margin ?? 0,
				margin_and_amount: {
					margin,
					marginAmount
				},
				selected_billing_period:
					curProduct?.selected_billing_period ??
					ProductBillingType.OneTime,
				selected_setup_price: curProduct?.selected_setup_price ?? 0
			});
			setRetailInfo(null);

			const product = curProduct as unknown as Product;
			const defaultPricing =
				pricing ??
				product?.pricing.find(pricing => {
					return selectedPricing === PricingOptions.Catalog
						? !pricing.distributor_pricing_details
						: selectedPricing === PricingOptions.Custom
						? pricing.vendor_name === selectedPricing
						: pricing.distributor_pricing_details &&
						  pricing.id === selectedPricing?.id;
				});

			form.setFieldsValue({
				vendor_name: defaultPricing?.vendor_name ?? ""
			});
			const distributorValue = defaultPricing?.distributor_pricing_details
				? (defaultPricing?.distributor_pricing_details
						.distributor as PricingRadioOptions) ?? null
				: PricingOptions.Catalog;
			const newSelectedPricing =
				defaultPricing?.distributor_pricing_details
					? defaultPricing
					: PricingOptions.Catalog;

			setSelectedDistributor(distributorValue);
			setSelectedPricing(newSelectedPricing);
		}
	};

	const onProductTypeChange = (productType?: ProductType) => {
		if (productType) {
			setCurrentProductType(productType);
			const tax_category_id = taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.findIndex(
						defaultProductType =>
							defaultProductType === (productType as ProductType)
					) > -1
			)?.id;
			const unit_of_measure = unitOfMeasureMapping?.find(
				mapping => mapping.item_type === productType
			)?.unit_of_measure;
			form.setFieldsValue({ unit_of_measure });
			if (tax_category_id) {
				form.setFieldsValue({ tax_category_id });
			} else {
				form.setFields([
					{
						name: "tax_category_id",
						value: "",
						errors: ["Please select tax category"]
					}
				]);
			}
			resetDynamicForm();
		}
	};

	const handleEtilizePricingChange = (
		pricingId:
			| string
			| PricingOptions.Catalog
			| PricingOptions.Custom
			| null
	) => {
		if (
			[PricingOptions.Catalog, PricingOptions.Custom].includes(
				pricingId as PricingOptions
			)
		) {
			setSelectedDistributor(pricingId as PricingOptions);
			setSelectedPricing(pricingId as PricingOptions);
			return;
		}
		const pricingList =
			selectedEtilizeProduct?.pricing ??
			(currentProduct as Product)?.pricing;
		const newSelectedPricing =
			pricingList.find(pricing => pricing.id === pricingId) || null;

		const newSelectedDistributor =
			newSelectedPricing?.distributor_pricing_details?.distributor ||
			null;

		setSelectedDistributor(newSelectedDistributor);
		setSelectedPricing(newSelectedPricing);
	};

	const sections: IFormSectionProps[] = [
		{
			isIndentFormItems: false,
			getMeta: () => {
				const canHaveMultiplePricingPlans =
					checkCanMultiplePricingPlansFor(currentProductType);

				return {
					formItemLayout: [6, 18],
					fields: [
						{
							key: "name",
							label: "Name",
							required: true,
							message: "Please enter product name",
							initialValue:
								drawerState === EProductDrawerState.ETILIZEADD
									? currentProduct?.name?.split(",")[0] ?? ""
									: currentProduct?.name,
							hasFeedback: true,
							widget: EWidgets.INPUT,
							widgetProps: {
								width: "full",
								placeholder: "Please enter product name"
							},
							rules: [
								{
									whitespace: true,
									message: "Product name cannot be empty"
								}
							]
						},
						...customFieldList
							?.map((customField, index) => {
								switch (customField.system_field_id) {
									case "item_vendor_name":
									case "item_vendor_sku_no":
									case "item_tax_type":
									case "item_show_individual_product_pricing":
									case "item_asin":
										return [];
									case "item_item_images":
										return {
											key: "product_item_images",
											wrapperCol: { span: 24 },
											render: ProductItemImages
										};
									case "item_pricing": {
										if (isAmazonProduct) {
											return {
												key: "product_item_pricing",
												wrapperCol: { span: 24 },
												render: () => {
													const pricingList =
														currentActiveProduct?.pricing ??
														(pricing
															? [pricing]
															: []);
													return (
														<Row className="mb-6">
															<Col span={6}>
																Pricing
															</Col>
															<Col span={18}>
																<DynamicPricingOptions
																	handlePricingsChange={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
																	pricing={
																		pricingList
																	}
																	selectedPricingId={
																		(
																			selectedPricing as ProductPricingPlan
																		)?.id
																	}
																	handlePricingIdChange={({
																		value
																	}) => {
																		const newPricing =
																			pricingList.find(
																				pricing =>
																					pricing.id ===
																					value
																			);
																		newPricing &&
																			setSelectedPricing(
																				newPricing
																			);
																	}}
																	handleAutoUpdatePricingChange={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
																	handleSelectedDistributorsChange={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
																	isAutoUpdateSwitchEnabled={
																		false
																	}
																	isAutoUpdatePricingEnabled={
																		false
																	}
																	handleRoundingDetailsChange={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
																	calculatedSellPrice={form.getFieldValue(
																		"sell_price"
																	)}
																	isAmazonProduct={
																		isAmazonProduct
																	}
																	isProductPageAndAmazonCatalog={
																		true
																	}
																/>
															</Col>
														</Row>
													);
												}
											};
										}
										const integratedDistributors =
											noOfDistributorsIntegrated(
												integrations
											);
										return showDynamicPricingSection
											? {
													key: "product_item_pricing",
													wrapperCol: { span: 24 },
													render: () => (
														<Row className="mb-6">
															<Col span={6}>
																Pricing
															</Col>
															<Col span={18}>
																<ZDistributorProductPricing
																	pricing={
																		selectedEtilizeProduct?.pricing ??
																		(
																			currentProduct as Product
																		)
																			?.pricing
																	}
																	selectedPricing={
																		selectedPricing
																	}
																	handlePricingChange={
																		handleEtilizePricingChange
																	}
																	integratedDistributors={
																		integratedDistributors
																	}
																/>
															</Col>
														</Row>
													)
											  }
											: canHaveMultiplePricingPlans
											? {
													key: "product_item_pricing",
													wrapperCol: { span: 24 },
													render: () => ProductPricing
											  }
											: [];
									}
									case "item_tax_category_id":
										return {
											key: "tax_category_id",
											label: "Tax Category",
											hasFeedback: true,
											required: true,
											message:
												"Please enter tax category",
											initialValue:
												getInitialTaxCategoryId(),
											widget: ({
												value,
												onChange
											}: ICustomFormItemProps) => (
												<TaxSelect
													id="tax_category_id"
													placeholder="Please select tax category"
													getPopupContainer={trigger =>
														trigger.parentNode
													}
													loading={isLoading}
													disabled={isLoading}
													data-testid="tax-category-select"
													dropdownRender={menu => (
														<div>
															{menu}
															<RoleBasedDisplayWrapper
																requiredRole={
																	UserRole.PrimaryRole
																}
															>
																{hasTaxEditPermission && (
																	<div
																		className="add-entity-action"
																		onMouseDown={$event => {
																			$event.preventDefault();
																		}}
																		onClick={
																			showAddTaxCategoryDrawer
																		}
																	>
																		Add Tax
																		Category
																	</div>
																)}
															</RoleBasedDisplayWrapper>
														</div>
													)}
													value={value}
													onChange={onChange}
												/>
											)
										};
									default: {
										const ProductFormItem =
											getProductFormItem({
												isLoading,
												customField,
												customFieldId:
													customField.system_field_id,
												form,
												onProductTypeChange,
												ProductBundle,
												CostPriceInputGroup,
												SellPriceInputGroup,
												getFieldValues,
												currentProduct,
												getInitialUnitOfMeasure,
												currentProductType,
												showIndividualProductPricing,
												setShowIndividualProductPricing,
												drawerState,
												showDynamicPricingSection,
												productPricingPlanList,
												isBundleEnhancementEnable:
													enable_bundles_enchancements,
												pricingDisabled,
												selectedPricing:
													selectedPricing as ProductPricingPlan
											});

										if (
											drawerState !==
											EProductDrawerState.BUNDLECHILDEDIT
										) {
											const productCustomField =
												currentProduct?.custom_fields?.find(
													field =>
														field.id ===
														customField.id
												);
											const customDistributorCustomFields =
												(
													selectedPricing as ProductPricingPlan
												)?.distributor_pricing_details
													?.custom_distributor_pricing_details
													?.custom_fields;
											const customDistributorCustomField =
												customDistributorCustomFields?.find(
													field =>
														field.id ===
														productCustomField?.id
												);
											const values =
												customDistributorCustomFields
													? customDistributorCustomField?.values
													: productCustomField?.values;

											const uniqueKeyValue: string =
												index.toString() + values?.[0];

											const isDataEditable =
												customField?.is_data_editable_field;

											return (
												ProductFormItem ?? {
													key: `custom_product_form_item_${
														!!customDistributorCustomField
															? uniqueKeyValue
															: index
													}`,
													render: () => (
														<ZRenderCustomFieldFormItem
															form={form}
															customField={
																customField
															}
															isLoading={
																isLoading
															}
															values={values}
															isReadonly={
																drawerState ===
																EProductDrawerState.EDITORCLONE
																	? ((
																			currentProduct as Product
																	  )
																			?.read_only &&
																			mode ===
																				EditOrCloneProductDrawerMode.EDIT) ||
																	  !isDataEditable
																	: false
															}
															customFieldList={
																customFieldList
															}
															formAlignment={[
																6, 18
															]}
														/>
													)
												}
											);
										} else {
											const productCustomField = (
												currentProduct as BundleProduct
											)?.item?.custom_fields?.find(
												field =>
													field.display_name ===
													customField.display_name
											);
											const values =
												productCustomField?.values;
											const isDataEditable =
												customField?.is_data_editable_field ??
												true;
											return (
												ProductFormItem ?? {
													key: `custom_product_form_item_${index}`,
													render: () => (
														<ZRenderCustomFieldFormItem
															form={form}
															customField={
																customField
															}
															isLoading={
																isLoading
															}
															values={values}
															isReadonly={
																!!isReadonly ||
																!isDataEditable
															}
															customFieldList={
																customFieldList
															}
															formAlignment={[
																6, 18
															]}
														/>
													)
												}
											);
										}
									}
								}
							})
							.flat()
					]
				};
			}
		}
	];

	const updatePricingInfoFromEtilize = () => {
		if (
			drawerState === EProductDrawerState.ADD &&
			!selectedEtilizeProduct
		) {
			ZMessage.error("No Etilize product selected");
			return;
		}

		const selectedProduct =
			drawerState === EProductDrawerState.ADD
				? selectedEtilizeProduct
				: (currentProduct as Product);

		const newSelectedPricing = selectedProduct?.pricing.find(pricing => {
			if (isAmazonProduct) {
				return (
					pricing.id === (selectedPricing as ProductPricingPlan)?.id
				);
			}
			return selectedPricing === PricingOptions.Catalog
				? !pricing.distributor_pricing_details
				: selectedPricing === PricingOptions.Custom
				? pricing.vendor_name === selectedPricing
				: (pricing.distributor_pricing_details ||
						pricing.amazon_business_pricing_details) &&
				  pricing.id === selectedPricing?.id;
		});

		const values = form.getFieldsValue([
			"selected_cost_price",
			"sell_price",
			"quantity",
			"should_add_setup_price_to_margin",
			"selected_setup_price",
			"type",
			"vendor_name",
			"vendor_sku_no",
			"asin"
		]);

		if (!newSelectedPricing) {
			// if user selects Custom plan, then reset these fields

			// Check whether the pricing radio was changed by changing radio or changing the cost price
			// checking if the current cost price is present in the pricing array
			// if present, then cost price isn't changed and user changed through radio options
			const isDistributorPricing = selectedProduct?.pricing.find(
				pricing => pricing.cost_price === values.selected_cost_price
			);

			if (isDistributorPricing) {
				form.setFieldsValue({
					selected_cost_price: 0,
					sell_price: 0,
					vendor_name: "",
					markup: defaultPreferences?.markup,
					margin: defaultPreferences?.margin,
					selected_setup_price: 0
				});
				setRetailInfo(null);
			}

			return;
		}

		const { markup, margin } = handleMarkupMargin(
			newSelectedPricing?.cost_price,
			newSelectedPricing?.sell_price,
			values.quantity,
			values.should_add_setup_price_to_margin,
			values.selected_setup_price
		);

		const calculatedMarkup =
			drawerState === EProductDrawerState.ETILIZEADD
				? defaultPreferences?.markup
				: markup;

		const calculateMargin =
			drawerState === EProductDrawerState.ETILIZEADD
				? defaultPreferences?.margin
				: margin;

		const calculatedSellPrice =
			drawerState === EProductDrawerState.ETILIZEADD ||
			newSelectedPricing.cost_price === newSelectedPricing.sell_price ||
			!newSelectedPricing.sell_price
				? (calculatedMarkup ?? 0) *
						0.01 *
						newSelectedPricing?.cost_price +
				  newSelectedPricing?.cost_price
				: newSelectedPricing.sell_price;

		form.setFieldsValue({
			selected_cost_price: newSelectedPricing.cost_price,
			sell_price: calculatedSellPrice,
			vendor_sku_no: newSelectedPricing.vendor_sku_no,
			vendor_name: newSelectedPricing.vendor_name,
			supplier_id: newSelectedPricing.supplier_id,
			markup: calculatedMarkup,
			margin: calculateMargin,
			selected_setup_price: newSelectedPricing.setup_price,
			asin: selectedProduct?.asin
		});

		const retailPrice = newSelectedPricing?.distributor_pricing_details
			?.retail_price
			? ({
					distributor:
						newSelectedPricing.distributor_pricing_details
							?.distributor,
					label: ratailPriceLabel[
						newSelectedPricing.distributor_pricing_details
							?.distributor
					],
					price: newSelectedPricing.distributor_pricing_details
						?.retail_price,
					vendor_name:
						newSelectedPricing.distributor_pricing_details
							.vendor_name
			  } as RetailPriceInfo)
			: null;
		setRetailInfo(retailPrice);
	};

	useComponentDidUpdate(() => {
		updatePricingInfoFromEtilize();
	}, [selectedPricing]);

	useEffect(() => {
		if (drawerOpen && currentProduct && !selectedEtilizeProduct) {
			const defaultSelectedDistributor =
				currentProduct?.type !== ProductType.Bundle
					? (currentProduct as Product)
							?.selected_pricing_distributor ??
					  PricingOptions.Catalog
					: null;
			setSelectedDistributor(defaultSelectedDistributor || null);

			if (
				defaultSelectedDistributor === null ||
				defaultSelectedDistributor === PricingOptions.Catalog
			) {
				if (isAmazonProduct) {
					handleEtilizePricingChange(
						(currentProduct as Product).pricing[0]?.id
					);
				} else {
					setSelectedPricing(defaultSelectedDistributor);
				}
			} else {
				const newProduct = currentProduct as Product;
				const newPricing =
					drawerState === EProductDrawerState.EDITORCLONE
						? PricingOptions.Catalog
						: defaultSelectedDistributor ===
						  Distributor.CustomDistributor
						? newProduct.pricing.find(
								pricing =>
									pricing.distributor_pricing_details
										?.custom_distributor_pricing_details
										?.custom_distributor_pricing_id ===
									newProduct
										.selected_custom_distributor_details
										?.selected_custom_distributor_pricing_id
						  )
						: newProduct.pricing.find(
								pricing =>
									pricing.distributor_pricing_details
										?.distributor ===
									defaultSelectedDistributor
						  );
				setSelectedPricing(newPricing ?? null);
			}
		}
	}, [drawerOpen, currentProduct, isAmazonProduct]);

	useEffect(() => {
		resetDynamicForm();
	}, [productPricingPlanList]);

	const resetPartialState = () => {
		form.resetFields();
		setCurrentActiveProductImage(null);
		setProductImageList([]);
		setProductImageUploadModalOpen(false);
		setProductImagePreviewModalOpen(false);
		setCurrentProductType(ProductType.Software);
		setCurrentProduct(null);
	};

	return {
		sections,
		getFieldValues,
		setMarginAndAmount,
		currentProduct,
		closeAddTaxCategoryDrawer,
		handleTaxCategoryDrawerOnSubmit,
		setCurrentProductType,
		customFieldList,
		productImageList,
		stopPropagation,
		resetPartialState,
		setProductImageList,
		setCurrentProduct,
		onProductTypeChange,
		showIndividualProductPricing,
		setShowIndividualProductPricing,
		resetDynamicForm,
		selectedDistributor,
		selectedPricing
	};
};

export default useCommonFunctions;
